/**
 * Customer-Scoped Editor API Client
 *
 * Same function signatures as the dashboard's api/editor.ts but routes
 * through customer-scoped endpoints that auto-detect the customer's website.
 */

import { apiGet, apiPost, apiRequest } from './client';
import type { EditorContentResponse } from '../editor/types';

const API_URL =
  import.meta.env.VITE_API_URL ||
  'https://niftee-orchestrator.jason-a6e.workers.dev';

// =============================================================================
// CONTENT
// =============================================================================

/**
 * Get the V4 content model for the customer's website.
 * No websiteId needed — session-scoped.
 */
export async function getEditorContent(
  _websiteId?: string
): Promise<EditorContentResponse> {
  return apiGet<EditorContentResponse>('/api/customer/editor/content');
}

/**
 * Get full page HTML for iframe preview.
 * Returns raw HTML string (not JSON).
 */
export async function getEditorPreview(
  _websiteId: string,
  pageSlug: string
): Promise<string> {
  const session = localStorage.getItem('niftee_portal_session');
  const headers: Record<string, string> = {};

  if (session) {
    try {
      const { token } = JSON.parse(session);
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    } catch {
      // Ignore
    }
  }

  const response = await fetch(
    `${API_URL}/api/customer/editor/preview/${pageSlug}`,
    { headers }
  );

  if (!response.ok) {
    throw new Error(`Failed to load preview: ${response.statusText}`);
  }

  return response.text();
}

// =============================================================================
// STAGING SESSION
// =============================================================================

interface StartSessionResponse {
  success: boolean;
  stagingId?: string;
  staging?: { id: string };
  isNew?: boolean;
  hasLock?: boolean;
  lockedBy?: string;
  message?: string;
}

/**
 * Start or resume a staging session.
 * Customer-scoped: no websiteId/userId needed in the request.
 */
export async function startStagingSession(
  _websiteId: string,
  _userId: string,
  _userName: string
): Promise<StartSessionResponse> {
  return apiPost<StartSessionResponse>('/api/customer/editor/staging/start');
}

/**
 * Save a single element change to staging.
 */
export async function saveStagingElement(
  _websiteId: string,
  elementId: string,
  oldValue: string,
  newValue: string
): Promise<{ success: boolean }> {
  return apiRequest<{ success: boolean }>('/api/customer/editor/staging/element', {
    method: 'PATCH',
    body: JSON.stringify({ elementId, oldValue, newValue }),
  });
}

/**
 * Refresh the edit lock (heartbeat).
 */
export async function refreshStagingLock(
  _websiteId: string
): Promise<{ success: boolean }> {
  return apiPost<{ success: boolean }>('/api/customer/editor/staging/lock/refresh');
}

/**
 * Release the edit lock.
 */
export async function releaseStagingLock(
  _websiteId: string
): Promise<{ success: boolean }> {
  return apiRequest<{ success: boolean }>('/api/customer/editor/staging/lock', {
    method: 'DELETE',
  });
}

// =============================================================================
// STUB EXPORTS (TODO: Implement these when AI editor features are completed)
// =============================================================================

export interface BusinessInfoUpdate {
  business_name?: string;
  tagline?: string;
  phone?: string;
  email?: string;
  address?: string;
}

/**
 * Send a chat message to Claude for AI editing (stub).
 */
export async function sendClaudeChatMessage(
  _websiteId: string,
  _message: string,
  _context?: unknown
): Promise<{ success: boolean; response?: string; changes?: unknown[] }> {
  console.warn('sendClaudeChatMessage is not yet implemented');
  return { success: false, response: 'AI editing not yet available in customer portal' };
}

/**
 * Switch the website theme (stub).
 */
export async function switchTheme(
  _websiteId: string,
  _themeId: string
): Promise<{ success: boolean }> {
  console.warn('switchTheme is not yet implemented');
  return { success: false };
}

/**
 * Update business info (stub).
 */
export async function updateBusinessInfo(
  _websiteId: string,
  _updates: BusinessInfoUpdate
): Promise<{ success: boolean }> {
  console.warn('updateBusinessInfo is not yet implemented');
  return { success: false };
}

/**
 * Upload a logo (stub).
 */
export async function uploadLogo(
  _websiteId: string,
  _file: File
): Promise<{ success: boolean; url?: string }> {
  console.warn('uploadLogo is not yet implemented');
  return { success: false };
}

/**
 * Remove the logo (stub).
 */
export async function removeLogo(
  _websiteId: string
): Promise<{ success: boolean }> {
  console.warn('removeLogo is not yet implemented');
  return { success: false };
}

/**
 * Update page SEO settings (stub).
 */
export async function updatePageSeo(
  _websiteId: string,
  _pageSlug: string,
  _seo: { title?: string; description?: string; keywords?: string }
): Promise<{ success: boolean }> {
  console.warn('updatePageSeo is not yet implemented');
  return { success: false };
}

/**
 * Revert all staging changes (stub).
 */
export async function revertAllChanges(
  _websiteId: string
): Promise<{ success: boolean; stagingId?: string }> {
  console.warn('revertAllChanges is not yet implemented');
  return { success: false };
}

// =============================================================================
// AI IMAGE GENERATION (standard editor route; the auth gate admits an owning
// customer session, so the portal calls it with the customer's real websiteId)
// =============================================================================

export interface GenerationLevers {
  setting?: 'home' | 'outdoor' | 'studio' | 'workplace' | 'community-space';
  mood?: 'warm-candid' | 'bright-energetic' | 'calm-serene' | 'professional';
  shot?: 'close-up' | 'medium' | 'wide';
  people?: 'none' | 'one' | 'pair' | 'small-group';
  representation?: string;
}

export interface GenerateImageRequest {
  intent: string;
  levers?: GenerationLevers;
  sectionType?: string;
  aspect?: 'landscape' | 'square' | 'portrait';
  slot?: string;
  previewOnly?: boolean;
  referenceUrl?: string;
  referencePermissionConfirmed?: boolean;
}

export interface GenerateImageResponse {
  success: boolean;
  prompt?: string;
  size?: string;
  estimatedCostUsd?: number;
  costUsd?: number;
  leversApplied?: string[];
  brandStyleAvailable?: boolean;
  usingReference?: boolean;
  usedReference?: boolean;
  url?: string;
  mediaId?: string;
  fromCache?: boolean;
  error?: string;
  contentPolicy?: boolean;
  needsPermissionConfirmation?: boolean;
}

export async function previewImagePrompt(
  websiteId: string,
  request: Omit<GenerateImageRequest, 'previewOnly'>
): Promise<GenerateImageResponse> {
  return apiPost<GenerateImageResponse>(`/api/editor/${websiteId}/generate-image`, { ...request, previewOnly: true });
}

export async function generateImage(
  websiteId: string,
  request: Omit<GenerateImageRequest, 'previewOnly'>
): Promise<GenerateImageResponse> {
  return apiPost<GenerateImageResponse>(`/api/editor/${websiteId}/generate-image`, { ...request, previewOnly: false });
}
